import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import {
  get,
  map,
  filter,
  includes,
  indexOf,
  size,
  forEach,
  flatten,
} from "lodash";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";

import { fetchGet } from "../../../api/fetch";
import DatePicker from "../../Common/DatePicker";
import MenuButton from "../../Common/MenuButton";
import FormSection from "../../Common/FormSection";
import ProfileSearchInput from "../../Common/ProfileSearchInput";
import ProfileMatchExpress from "../CorrectConfirmValue/ProfileMatchExpress";
import { driveInOut } from "../../../constants/reducers";
import { getLov } from "../../../utils/lov";
import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";

const styles = {
  formContainer: {
    padding: "24px",
  },
  column: {
    width: "340px",
  },
  formLabel: {
    fontSize: "1em",
    color: `${colors.black87} !important`,
    fontWeight: 500,
    marginTop: 0,
    height: "20px",
    lineHeight: "20px",
  },
  formControlLabel: {
    fontSize: "1em",
    color: colors.black54,
  },
  formControlRoot: {
    height: "30px",
  },
  profileFormContainer: {
    width: "300px",
    minWidth: "300px",
    marginRight: "24px",
  },
  plusIcon: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
};

const DriveInOutForm = (props) => {
  const {
    classes,
    companies,
    user,
    language,
    eventGroupTypeId,
    formValues,
    handleChange,
    setDefaultValues,
  } = props;

  const [carriers, setCarriers] = React.useState();
  const [profileTypes, setProfileTypes] = React.useState();
  const [profileType, setProfileType] = React.useState();

  const siteOptions = filter(
    map(companies, (c) => ({
      ...c,
      options: filter(
        get(c, ["sites"]),
        (s) =>
          size(
            filter(
              get(s, ["zones"]),
              (z) =>
                z.active &&
                z.is_vehicle_access &&
                (eventGroupTypeId === 2
                  ? z.is_entrance
                  : eventGroupTypeId === 3
                    ? z.is_exit
                    : true)
            )
          ) > 0
      ),
    })),
    (c) => size(c.options) > 0
  );

  React.useEffect(() => {
    initDefaultValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initDefaultValues = async () => {
    let defaultValues = {};
    if (!get(formValues, ["site"])) {
      if (get(activeSite, ["id"])) {
        defaultValues.site = get(activeSite, ["id"]);
      } else if (get(user, ["assignedSite"])) {
        defaultValues.site = get(user, ["assignedSite"]);
      } else if (size(siteOptions) === 1) {
        defaultValues.site = get(siteOptions, [0, "id"]);
      }
    }
    if (!get(formValues, ["profile"])) {
      try {
        let p = await fetchGet("profiles/00000000-0000-0000-0000-000000000000");
        defaultValues.profile = p;
      } catch (e) {
        console.log(e);
      }
    }
    setDefaultValues(defaultValues);
  };

  const activeSite = get(
    filter(
      flatten(map(companies, (c) => c.sites)),
      (s) => s.id === get(formValues, ["site"])
    ),
    [0]
  );

  const zones = filter(
    get(activeSite, ["zones"]),
    (z) =>
      z.active &&
      z.is_vehicle_access &&
      (eventGroupTypeId === 2
        ? z.is_entrance
        : eventGroupTypeId === 3
          ? z.is_exit
          : true)
  );

  React.useEffect(() => {
    if (
      !get(formValues, ["zone"]) ||
      !includes(
        map(zones, (z) => z.id),
        get(formValues, ["zone"])
      )
    ) {
      if (size(zones) === 1) {
        handleChange("zone", get(activeSite, ["id"]), get(zones, [0, "id"]));
      } else {
        handleChange("zone", get(activeSite, ["id"]), null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones]);

  React.useEffect(() => {
    fetchCarriers();
    fetchProfileTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (profileType) {
      document.getElementById("autoFocusInput").focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileType]);

  const excludeProfileIds = map(
    filter(get(formValues, ["profiles"]), (p) => get(p, ["id"])),
    (p) => get(p, ["id"])
  );

  const fetchCarriers = async () => {
    try {
      setCarriers(await getLov(89));
    } catch (e) {
      console.log(e);
    }
  };

  const fetchProfileTypes = async () => {
    try {
      let res = await getLov("profileTypes");
      res = filter(
        res,
        (item) => item.active && includes([1, 2, 3, 4], item.id)
      );
      setProfileTypes(res);
    } catch (e) {
      console.log(e);
    }
  };

  const getProfileTypeDisplay = (profile) => {
    let index = indexOf(
      map(
        filter(
          get(formValues, ["profiles"]),
          (p) => get(profile, ["type", "id"]) === get(p, ["type", "id"])
        ),
        (p) => p.id
      ),
      profile.id
    );

    if (get(profile, ["type", "id"]) === 1) {
      if (index === 0) {
        return I18n.get("site_driver2");
      } else {
        return `${I18n.get("site_passenger")} ${index}`;
      }
    } else if (includes([3, 4], get(profile, ["type", "id"]))) {
      return `${get(profile, ["type", `name_${language}`])} ${index + 1}`;
    }

    return get(profile, ["type", `name_${language}`]);
  };

  const renderProfile = (p) => {
    return (
      <Box
        style={{
          margin: "10px 0px",
          backgroundColor: colors.lightGrey,
        }}
      >
        <Grid
          container
          alignItems="center"
          style={{ padding: "8px 8px 0px 8px", marginBottom: "-8px" }}
        >
          <Grid item className="flexCenter">
            <img
              src={
                get(p, ["type", "id"]) === 1
                  ? images.humanIconBlack
                  : get(p, ["type", "id"]) === 2
                    ? images.truckIconBlack
                    : get(p, ["type", "id"]) === 3
                      ? images.trailerIconBlack
                      : get(p, ["type", "id"]) === 4
                        ? images.containerIconBlack
                        : null
              }
              height="16px"
              style={{ marginRight: "7px", maxWidth: "30px" }}
              alt="icon"
            />
          </Grid>
          <Grid item className="bold-500">
            {getProfileTypeDisplay(p)}
          </Grid>
          <Grid item xs style={{ textAlign: "right" }}>
            <img
              src={images.closeIcon}
              width="10px"
              alt="close"
              style={{ cursor: "pointer" }}
              onClick={() => handleChange("removeProfile", null, p)}
            />
          </Grid>
        </Grid>
        <ProfileMatchExpress
          profile={p}
          style={{
            padding: 0,
            margin: "10px 0px",
          }}
        />
      </Box>
    );
  };

  const getOtherProfiles = () => {
    let driverExcluded = false;
    let vehicleExcluded = false;
    let profiles = [];
    forEach(get(formValues, ["profiles"]), (p) => {
      if (get(p, ["type", "id"]) === 1) {
        if (driverExcluded) {
          profiles.push(p);
        } else {
          driverExcluded = true;
        }
      } else if (get(p, ["type", "id"]) === 2) {
        if (vehicleExcluded) {
          profiles.push(p);
        } else {
          vehicleExcluded = true;
        }
      } else {
        profiles.push(p);
      }
    });
    return profiles;
  };

  const renderProfilesList = (index, op) => {
    let profiles = getOtherProfiles();
    profiles =
      op === "sub"
        ? filter(profiles, (p, i) => i < index)
        : filter(profiles, (p, i) => i >= index);
    profiles = filter(profiles, (p) => p !== undefined);
    return (
      <>
        {map(profiles, (p) => (
          <React.Fragment key={p.id}>{renderProfile(p)}</React.Fragment>
        ))}
      </>
    );
  };

  return (
    <Grid container wrap="nowrap" className={classes.formContainer}>
      <Grid item className={classes.column} style={{ marginRight: "20px" }}>
        <Grid container alignItems="flex-end" style={{ marginBottom: "30px" }}>
          <Grid
            item
            className="bold-500 black87"
            style={{ marginRight: "20px" }}
          >
            {I18n.get("site_profiles")}
          </Grid>
          <MenuButton
            items={map(profileTypes, (p) => ({
              ...p,
              icon:
                get(p, ["id"]) === 1
                  ? images.humanIconBlack
                  : get(p, ["id"]) === 2
                    ? images.truckIconBlack
                    : get(p, ["id"]) === 3
                      ? images.trailerIconBlack
                      : get(p, ["id"]) === 4
                        ? images.containerIconBlack
                        : null,
            }))}
            onClickItem={(item) => setProfileType(get(item, ["id"]))}
            noArrow
          />
        </Grid>
        {size(
          filter(
            get(formValues, ["profiles"]),
            (p) => get(p, ["type", "id"]) === 1
          )
        ) === 0 ? (
          <ProfileSearchInput
            profileType={1}
            title={"driver"}
            selectProfile={(profile) =>
              handleChange("addProfile", null, profile)
            }
            noMinHeight
            excludeProfileIds={excludeProfileIds}
          />
        ) : (
          renderProfile(
            get(
              filter(
                get(formValues, ["profiles"]),
                (p) => get(p, ["type", "id"]) === 1
              ),
              [0]
            )
          )
        )}
        {size(
          filter(
            get(formValues, ["profiles"]),
            (p) => get(p, ["type", "id"]) === 2
          )
        ) === 0 ? (
          <ProfileSearchInput
            profileType={2}
            title={"vehicle"}
            selectProfile={(profile) =>
              handleChange("addProfile", null, profile)
            }
            noMinHeight
            excludeProfileIds={excludeProfileIds}
          />
        ) : (
          renderProfile(
            get(
              filter(
                get(formValues, ["profiles"]),
                (p) => get(p, ["type", "id"]) === 2
              ),
              [0]
            )
          )
        )}
        {renderProfilesList(2, "sub")}
        {profileType && size(getOtherProfiles()) < 2 && (
          <ProfileSearchInput
            profileType={profileType}
            title2={get(
              filter(profileTypes, (i) => get(i, ["id"]) === profileType),
              [0, `name_${language}`]
            )}
            selectProfile={(profile) => {
              handleChange("addProfile", null, profile);
              setProfileType();
            }}
            excludeProfileIds={excludeProfileIds}
            id="autoFocusInput"
          />
        )}
      </Grid>
      {(size(getOtherProfiles()) > 2 ||
        (profileType && size(getOtherProfiles()) === 2)) && (
          <Grid
            item
            className={classes.column}
            style={{ marginRight: "20px", paddingTop: "44px" }}
          >
            {renderProfilesList(2, "sup")}
            {profileType &&
              size(getOtherProfiles()) >= 2 &&
              size(getOtherProfiles()) < 8 && (
                <Box style={{ marginTop: "12px" }}>
                  <ProfileSearchInput
                    profileType={profileType}
                    title2={get(
                      filter(profileTypes, (i) => get(i, ["id"]) === profileType),
                      [0, `name_${language}`]
                    )}
                    selectProfile={(profile) => {
                      handleChange("addProfile", null, profile);
                      setProfileType();
                    }}
                    excludeProfileIds={excludeProfileIds}
                    id="autoFocusInput"
                  />
                </Box>
              )}
          </Grid>
        )}
      <Grid item className={classes.column} style={{ marginLeft: "20px" }}>
        <FormSection
          formValue={[get(formValues, ["site"]) || ""]}
          setFormValue={handleChange}
          title={"site"}
          id={"site"}
          type="select"
          options={{
            required: true,
            options: siteOptions,
            categories: true,
          }}
        />
        <FormSection
          formValue={[get(formValues, ["zone"]) || ""]}
          setFormValue={handleChange}
          title={"zone"}
          id={"zone"}
          type="select"
          options={{
            comboBox: true,
            required: true,
            options: zones,
          }}
        />
        <FormSection
          formValue={[get(formValues, ["carrier"]) || ""]}
          setFormValue={handleChange}
          attribute={{ id: 89 }}
          id={"carrier"}
          type="select"
          options={{
            comboBox: true,
            options: map(carriers, (c) => ({
              id: get(c, ["id"]),
              name: get(c, [`name_${props.language}`]),
            })),
          }}
        />
        <Grid item style={{ marginRight: "60px" }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" classes={{ root: classes.formLabel }}>
              {I18n.get("site_dateTime")}
            </FormLabel>
            <RadioGroup
              name="date"
              value={get(formValues, ["dateType"])}
              onChange={(e) => handleChange("dateType", null, e.target.value)}
            >
              <FormControlLabel
                value="current"
                control={<Radio color="primary" />}
                label={I18n.get("site_currentDateTime")}
                classes={{
                  label: classes.formControlLabel,
                  root: classes.formControlRoot,
                }}
              />
              <FormControlLabel
                value="custom"
                control={<Radio color="primary" />}
                label={I18n.get("site_pastDateTime")}
                classes={{
                  label: classes.formControlLabel,
                  root: classes.formControlRoot,
                }}
              />
            </RadioGroup>
          </FormControl>
          {get(formValues, ["dateType"]) === "custom" && (
            <DatePicker
              name="date"
              value={get(formValues, ["date"])}
              onChange={(id, value) => handleChange("date", null, value)}
              width="100%"
            />
          )}
        </Grid>
        <FormSection
          formValue={[get(formValues, ["note"]) || ""]}
          setFormValue={handleChange}
          title={"comments"}
          id={"note"}
          type="input"
          options={{
            multiline: true,
            rows: 5,
          }}
          style={{ marginTop: "20px" }}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
    activeSite: get(state, ["siteEntranceExit", driveInOut, "activeSite"]),
    companies: get(state, ["user", "companies"]),
    user: get(state, ["user"]),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(DriveInOutForm));
